import React, { useState, useEffect } from "react";
import { IconButton, Input, Option, Select, Spinner, Tooltip, Typography, Menu, MenuHandler, MenuList, MenuItem } from "@material-tailwind/react";
import { useQueryClient } from 'react-query';
import { Edit2Icon, PhoneCallIcon, MagnetIcon, TrashIcon, XIcon, EyeIcon, MinusIcon, ArrowUpIcon, ArrowDownIcon, MessageCircleIcon, BellRingIcon, PencilLineIcon } from "lucide-react";
import { DeleteLead } from "../../services/backend";
import moment from "moment";
import { convertToPresentableText, Notify, NotifyError } from "../../utils/functions";
import {
	DEAL_STAGES,
	DEAL_STAGES_ENUM,
	MARKETING_CHANNELS_ENUM
} from "../../utils/enums";
import { usePermissions } from '../../custom-hooks/use-permissions';
import ReceptionistLead from '../receptionist.lead';

// Import components
import LeadFormModal from "./leadFormModal";
import EngagementFormModal from "./engagementFormModal";
import { useGetLeads } from "../../custom-hooks/use-leads";
import Rating from "../../components/rating";
import { InterpretLeadStatus } from "../../utils/ui-interpreters";
import Pagination from "../../components/pagination";
import { PageHeader } from "../../components";
import { useConfirm } from "../../custom-hooks/confirm-dialog";

// The most recent follow-up logged against a lead, or undefined if it has none.
const lastEngagement = (lead) => lead.engagementHistory?.slice(-1)[0];

// A follow-up is only outstanding when the flag is set on the latest engagement: if a
// newer engagement exists, the follow-up it asked for has already been done. Winning the
// deal resolves it the same way, so a WON lead never shows as pending — the backend clears
// the flag on conversion, and this keeps the table right for leads won before that.
const isFollowUpPending = (lead) =>
	lead?.leadStatus !== DEAL_STAGES.WON && lastEngagement(lead)?.followUpRequired === true;

// How long after logging a follow-up it stays correctable.
const EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;

// When an engagement was actually recorded. Its `date` field is user-entered and freely
// backdated, so it cannot answer this — but every subdocument gets an ObjectId, and the
// first 4 bytes of an ObjectId are its creation time in seconds.
const engagementCreatedAt = (engagement) => {
	const id = engagement?._id;
	if (typeof id !== 'string' || !/^[0-9a-fA-F]{24}$/.test(id)) return null;
	return new Date(parseInt(id.substring(0, 8), 16) * 1000);
};

const isLastFollowUpEditable = (lead) => {
	const createdAt = engagementCreatedAt(lastEngagement(lead));
	return !!createdAt && Date.now() - createdAt.getTime() < EDIT_WINDOW_MS;
};

const LeadManagementPage = () => {
	const { can } = usePermissions();
	const queryClient = useQueryClient();
	// State variables
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [engagementModalOpen, setEngagementModalOpen] = useState(false);
	const [selectedEngagement, setSelectedEngagement] = useState(null);
	const [selectedLeadId, setSelectedLeadId] = useState(null);
	// Status of the lead the engagement modal is open on — WON hides the Follow-up
	// Required checkbox, since a won deal has no callback left to schedule.
	const [selectedLeadStatus, setSelectedLeadStatus] = useState(null);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isReadOnly, setIsReadOnly] = useState(false);
	const [refreshRequired, setRefreshRequired] = useState(0);

	// Filters
	const [filterStatus, setFilterStatus] = useState('');
	const [filterSource, setFilterSource] = useState('');
	const [filterHasFollowUps, setFilterHasFollowUps] = useState('');
	const [searchTerm, setSearchTerm] = useState('');

	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [sort, setSort] = useState('-createdDate');

	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [isTyping, setIsTyping] = useState(false);
	const [filters, setFilters] = useState({});
	const { confirm, ConfirmationDialog } = useConfirm();

	const {
		data: list,
		isLoading: isLeadLoading,
		// error: leadsError, 
		refetch
	} = useGetLeads({
		_page: page,
		_limit: limit,
		_search: debouncedSearch,
		_sort: sort,
		...filters
	});

	const {
		total,
		totalPages
	} = list || {};

	// Debounce the search input to avoid making API calls on every keystroke
	useEffect(() => {
		setIsTyping(true);
		const timer = setTimeout(() => {
			setDebouncedSearch(searchTerm);
			setIsTyping(false);
		}, 500); // Wait for 500ms after the customer stops typing

		return () => {
			clearTimeout(timer);
		};
	}, [searchTerm]);

	// Reset to page 1 when search changes
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, filters]);

	useEffect(() => {
		const fetchLeads = async () => {
			try {
				// if (refreshRequired > 1) {
				await refetch();
				// }
			} catch (error) {
				console.error('Error fetching leads:', error);
				NotifyError('Failed to fetch leads', error);
			}
		};

		fetchLeads();
	}, [refreshRequired, refetch]);

	// Open modal for adding a new lead
	const openAddLeadModal = () => {
		setFormModalOpen(true);
		setIsUpdating(false);
		setSelectedLeadId(null);
		setIsReadOnly(false);
	};

	// Open modal for updating a lead
	const openUpdateLeadModal = (leadId, readOnly) => {
		setFormModalOpen(true);
		setIsUpdating(true);
		setSelectedLeadId(leadId);
		setIsReadOnly(readOnly);
	};

	// Open modal for adding engagement
	const openAddEngagementModal = (lead) => {
		setSelectedLeadId(lead._id);
		setSelectedLeadStatus(lead.leadStatus);
		setSelectedEngagement(null);
		setEngagementModalOpen(true);
	};

	// Open the same modal prefilled, to correct the lead's most recent follow-up
	const openEditEngagementModal = (lead) => {
		setSelectedLeadId(lead._id);
		setSelectedLeadStatus(lead.leadStatus);
		setSelectedEngagement(lastEngagement(lead));
		setEngagementModalOpen(true);
	};

	// Delete lead
	const handleDeleteLead = async (leadId) => {
		const isConfirmed = await confirm({
			title: 'Delete Lead',
			message: 'Are you sure you want to delete this lead?',
			confirmText: 'Delete',
			cancelText: 'Cancel',
			confirmBtnColor: 'red',
		});
		if (isConfirmed) {
			try {
				await DeleteLead(leadId);
				queryClient.invalidateQueries('leads');
				setRefreshRequired(refreshRequired + 1);
				Notify('Lead deleted successfully', 'success');
			} catch (error) {
				console.error('Error deleting lead', error);
				NotifyError('Failed to delete lead', error);
			}
		}
	};

	// Handle filtering
	const handleFilterChange = (filter, value) => {
		if (filter === 'status') {
			setFilterStatus(value);
			setFilters({ ...filters, leadStatus: value });
		} else if (filter === 'source') {
			setFilterSource(value);
			setFilters({ ...filters, leadSource: value });
		} else if (filter === 'hasFollowUps') {
			setFilterHasFollowUps(value);
			setFilters({ ...filters, hasFollowUps: value });
		}
	};

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};


	const TABLE_COLUMNS = [
		{
			label: 'Name',
			key: 'name',
			sortable: true,
			sort: sort === "fullName" ? "-fullName" : "fullName"
		},
		{
			label: 'Status',
			key: 'leadStatus',
			sortable: true,
			sort: sort === "leadStatus" ? "-leadStatus" : "leadStatus"
		},
		{
			label: 'Follow-ups',
			key: 'followUps',
			sortable: false,
			sort: null
		},
		{
			label: 'Phone',
			key: 'phoneNumber',
			sortable: true,
			sort: sort === "phoneNumber" ? "-phoneNumber" : "phoneNumber"
		},
		{
			label: 'Source',
			key: 'leadSource',
			sortable: true,
			sort: sort === "leadSource" ? "-leadSource" : "leadSource"
		},
		{
			label: 'Requirements',
			key: 'notes',
			sortable: false,
			sort: null
		},
		{
			label: 'Connection',
			key: 'connectionDate',
			sortable: true,
			sort: sort === "connectionDate" ? "-connectionDate" : "connectionDate"
		},
		{
			label: 'Actions',
			key: 'actions',
			sortable: false,
			sort: null
		}
	];

	if (!can('FX11', 'manage') && can('FX11', 'add_only')) {
		return <ReceptionistLead />;
	}

	return (
		<div>
			<div className="m-5 p-5 border rounded-3xl">
				<PageHeader
					title="Lead Management"
					isLoading={isLeadLoading}
					actionOnClick={openAddLeadModal}
					actionType="add"
					actionText="Add New Lead"
				/>

				{/* Filters */}
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-4 my-4">
					<Input
						label="Search Leads"
						value={searchTerm}
						onChange={handleSearchChange}
						icon={isTyping ? <Spinner className="h-4 w-4" /> : <XIcon className={searchTerm ? "cursor-pointer" : "hidden"} size={16} onClick={() => setSearchTerm('')} />}
					/>
					<Select
						label="Filter by Status"
						value={filterStatus}
						onChange={(value) => handleFilterChange('status', value)}
						selected={(elem) =>
							elem ? (
								<div className="flex items-center gap-2">
									<div>
										<span>{convertToPresentableText(filterStatus)}</span>
									</div>
									<div>
										<XIcon className="cursor-pointer" size={12} onClick={(e) => {
											e.stopPropagation();
											handleFilterChange('status', '');
										}} />
									</div>
								</div>
							) : null
						}
					>
						{DEAL_STAGES_ENUM.map((status, index) => (
							<Option key={index} value={status}>
								{convertToPresentableText(status)}
							</Option>
						))}
					</Select>
					<Select
						label="Filter by Source"
						value={filterSource}
						onChange={(value) => handleFilterChange('source', value)}
						selected={(elem) =>
							elem ? (
								<div className="flex items-center gap-2">
									<div>
										<span>{convertToPresentableText(filterSource)}</span>
									</div>
									<div>
										<XIcon className="cursor-pointer" size={12} onClick={(e) => {
											e.stopPropagation();
											handleFilterChange('source', '');
										}} />
									</div>
								</div>
							) : null
						}
					>
						{MARKETING_CHANNELS_ENUM.map((source, index) => (
							<Option key={index} value={source}>
								{convertToPresentableText(source)}
							</Option>
						))}
					</Select>
					<Select
						label="Filter by Follow-up"
						value={filterHasFollowUps}
						onChange={(value) => handleFilterChange('hasFollowUps', value)}
						selected={(elem) =>
							elem ? (
								<div className="flex items-center gap-2">
									<div>
										<span>
											{filterHasFollowUps === 'true' ? 'Has Follow-ups' :
											 filterHasFollowUps === 'required' ? 'Follow-up Required' :
											 filterHasFollowUps === 'false' ? 'No Follow-ups' :
											 filterHasFollowUps === '5+' ? 'More than 5 Follow-ups' :
											 filterHasFollowUps === '1' ? '1 Follow-up' :
											 `${filterHasFollowUps} Follow-ups`}
										</span>
									</div>
									<div>
										<XIcon className="cursor-pointer" size={12} onClick={(e) => {
											e.stopPropagation();
											handleFilterChange('hasFollowUps', '');
										}} />
									</div>
								</div>
							) : null
						}
					>
						<Option value="true">Has Follow-ups</Option>
						<Option value="required">Follow-up Required</Option>
						<Option value="false">No Follow-ups</Option>
						<Option value="1">1 Follow-up</Option>
						<Option value="2">2 Follow-ups</Option>
						<Option value="3">3 Follow-ups</Option>
						<Option value="4">4 Follow-ups</Option>
						<Option value="5">5 Follow-ups</Option>
						<Option value="5+">More than 5 Follow-ups</Option>
					</Select>
				</div>

				{isLeadLoading ? (
					<div className="flex justify-center">
						<Spinner />
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-max table-auto text-left">
							<thead className="text-sm">
								<tr className="py-3">
									<th colSpan={TABLE_COLUMNS.length} className="py-3 text-xs font-light text-orange-900">
										Showing <span className="font-bold">{limit < total ? limit : total}</span> Business Leads of <span className="font-bold">{total}</span>
									</th>
								</tr>
								<tr className="py-3 uppercase">
									{
										TABLE_COLUMNS.map((column, index) => (
											<th
												key={index}
												className={"cursor-pointer py-3"}
												onClick={() => setSort(column.sortable ? column.sort : sort)}
											>
												<Typography
													variant="small"
													color="blue-gray"
													className="flex items-center gap-2 font-normal leading-none opacity-70">
													{column.label} {column.sortable ? (column.sort.startsWith('-') ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />) : ''}
												</Typography>
											</th>
										))
									}
								</tr>
							</thead>
							<tbody>
								{list?.leads?.length === 0 ? (
									<tr>
										<td colSpan={TABLE_COLUMNS.length} className="py-6 text-center text-gray-500">
											No leads found matching your filters.
										</td>
									</tr>
								) : (
									list?.leads?.map((lead, index) => (
										<tr key={index} className="border-t hover:bg-gray-50 button rounded-full">
											<td className="font-thin py-3 text-sm">
												<div>
													<div className="font-bold">{lead.fullName}</div>
													<div className="flex items-center gap-1 font-bold">
														<Rating rating={lead.score / 20} /> <MinusIcon size={12} /> {lead.score} <div className="text-xs font-thin text-gray-900">{lead.companyName}</div>
													</div>
												</div>
											</td>
											<td>
												<div className="flex">
													{InterpretLeadStatus(lead.leadStatus)}
												</div>
											</td>
											<td className="font-thin py-3 text-sm">
												{lead.engagementHistory?.length > 0 ? (
													<Tooltip className="border border-blue-gray-50 bg-white px-4 py-3 shadow-xl shadow-black/10 text-black" content={
														<div className="flex flex-col gap-1 max-w-xs">
															<div>
																Last follow-up {convertToPresentableText(lastEngagement(lead)?.contactMethod)} on <strong>{moment(lastEngagement(lead)?.date).format('lll')}</strong>
															</div>
															{(lastEngagement(lead)?.outcome || lastEngagement(lead)?.notes) && (
																<div className="line-clamp-2 break-words text-gray-700">
																	{lastEngagement(lead)?.outcome || lastEngagement(lead)?.notes}
																</div>
															)}
															{isFollowUpPending(lead) && (
																<div className="font-medium text-yellow-800">Follow-up required</div>
															)}
														</div>
													} placement="bottom">
														<div className="flex items-center gap-1">
															{isFollowUpPending(lead)
																? <BellRingIcon className="text-amber-600" size={12} />
																: <MagnetIcon className="text-green-600" size={12} />}
															<span className="font-bold">{lead.engagementHistory.length}</span>
															{isFollowUpPending(lead) && (
																<span className="ml-1 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
																	Required
																</span>
															)}
														</div>
													</Tooltip>
												) : (
													<span className="text-gray-500">None</span>
												)}
											</td>
											{/* <td >
												<div className="flex">
													{InterpretEngagementLevel(lead.engagementLevel)}
												</div>
											</td> */}
											<td className="font-thin py-3 text-sm">
												{lead.phoneNumber ? (
													<Menu placement="bottom-start">
														<MenuHandler>
															<span className="hover:underline text-blue-700 font-mono tabular-nums cursor-pointer">
																{lead.countryCode} {lead.phoneNumber}
															</span>
														</MenuHandler>
														<MenuList className="p-1 min-w-[120px]">
															<a href={`tel:${lead.countryCode}-${lead.phoneNumber}`} className="outline-none">
																<MenuItem className="flex items-center gap-2 text-sm text-gray-700">
																	<PhoneCallIcon size={14} /> Call Phone
																</MenuItem>
															</a>
															<a href={`whatsapp://send?phone=${(lead.countryCode + lead.phoneNumber).replace(/\D/g, '')}`} className="outline-none">
																<MenuItem className="flex items-center gap-2 text-sm text-green-600">
																	<MessageCircleIcon size={14} /> WhatsApp (App)
																</MenuItem>
															</a>
															<a href={`https://web.whatsapp.com/send?phone=${(lead.countryCode + lead.phoneNumber).replace(/\D/g, '')}`} target="whatsapp_window" className="outline-none">
																<MenuItem className="flex items-center gap-2 text-sm text-green-600">
																	<MessageCircleIcon size={14} /> WhatsApp (Web)
																</MenuItem>
															</a>
														</MenuList>
													</Menu>
												) : (
													<span className="text-gray-500">Not added</span>
												)}
											</td>
											<td className="font-thin py-3 text-sm">
												{convertToPresentableText(lead.leadSource)}
											</td>
											<td className="font-thin py-3 px-2 text-xs whitespace-pre-wrap">
												{lead.notes}
											</td>
											<td className="font-thin  text-xs">
												<Tooltip className="border border-blue-gray-50 bg-white px-4 py-3 shadow-xl shadow-black/10 text-black" content={
													<div className="flex flex-col gap-1">
														<div>
															Generated <strong>{moment(lead.connectionDate).format('lll')}</strong>
														</div>
														<div>
															Last contacted <strong>{lead.lastContactDate ? moment(lead.lastContactDate).format('lll') : 'Never'}</strong>
														</div>
													</div>
												} placement="bottom">
													<div>
														<div>
															{lead.connectionDate ? moment(lead.connectionDate).format('lll') : 'Never'}
														</div>
														<div className="flex items-center gap-1">
															<PhoneCallIcon color="blue" size={12} /> {lead.lastContactDate ? moment(lead.lastContactDate).format('lll') : 'Never'}
														</div>
													</div>
												</Tooltip>
											</td>
											<td className="flex gap-1 py-3">
												<Tooltip content="View Lead Status" placement="left">
													<IconButton
														size="sm"
														className="rounded-full"
														color="blue"
														onClick={() => openUpdateLeadModal(lead._id, true)}
													>
														<EyeIcon size={12} />
													</IconButton>
												</Tooltip>
												<Tooltip content="Update Lead" placement="bottom">
													<IconButton
														size="sm"
														color="orange"
														className="rounded-full"
														disabled={lead.leadStatus === DEAL_STAGES.WON || lead.leadStatus === DEAL_STAGES.DO_NOT_CONTACT}
														onClick={() => openUpdateLeadModal(lead._id, false)}
													>
														<Edit2Icon size={12} />
													</IconButton>
												</Tooltip>
												<Tooltip content="Add Follow-up" placement="bottom">
													<IconButton
														size="sm"
														className="rounded-full"
														color="green"
														disabled={lead.leadStatus === DEAL_STAGES.DO_NOT_CONTACT}
														onClick={() => openAddEngagementModal(lead)}
													>
														<MagnetIcon size={12} />
													</IconButton>
												</Tooltip>
												{isLastFollowUpEditable(lead) && (
													<Tooltip content="Edit Last Follow-up" placement="right">
														<IconButton
															size="sm"
															className="rounded-full"
															color="blue-gray"
															onClick={() => openEditEngagementModal(lead)}
														>
															<PencilLineIcon size={12} />
														</IconButton>
													</Tooltip>
												)}
												<IconButton
													size="sm"
													className="rounded-full hidden"
													color="red"
													onClick={() => handleDeleteLead(lead._id)}
												>
													<TrashIcon size={12} />
												</IconButton>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
						{
							totalPages > 1 &&
							<Pagination
								page={page}
								totalPages={totalPages}
								limit={limit}
								onChangePage={setPage}
								onChangeLimit={setLimit}
							/>
						}
					</div>
				)}

				{/* Lead Form Modal (Add/Edit) */}
				<LeadFormModal
					isOpen={formModalOpen}
					onClose={() => setFormModalOpen(false)}
					leadId={selectedLeadId}
					isUpdating={isUpdating}
					onSuccess={() => {
						setRefreshRequired(refreshRequired + 1);
						queryClient.invalidateQueries('leads');
					}}
					readOnly={isReadOnly}
				/>

				{/* Engagement Form Modal (Add/Edit) */}
				<EngagementFormModal
					isOpen={engagementModalOpen}
					engagement={selectedEngagement}
					onClose={() => setEngagementModalOpen(false)}
					leadId={selectedLeadId}
					leadStatus={selectedLeadStatus}
					onSuccess={() => {
						setRefreshRequired(refreshRequired + 1);
						queryClient.invalidateQueries('leads');
					}}
				/>
			</div>
			<ConfirmationDialog />
		</div>
	);
};

export default LeadManagementPage;